import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button } from 'components/Button';
import {
  QUOTE_NEW_RETURN_FEES,
  QUOTE_NEW_RETURN_SHIPPING_OPTIONS,
} from 'gql/newRequest';
import {
  PREPARE_RETURN_SHIPPING_CONTACT,
  UPDATE_RETURN_ORDER_CONTACT,
} from 'gql/mutations';
import {
  RequestOrder,
  NewRequestItemInput,
  NewReturnPolicy,
  NewReturnFeeQuote,
  NewReturnShippingQuote,
  NewReturnShippingOption,
  NewReturnShippingSelection,
} from 'types/newRequest';

type DeflectionOffer = NewReturnPolicy['deflectionOffers'][number];

export interface ReturnRequestSelection {
  requestedResolution: string;
  deflectionOption?: string;
  selectedShippingOption?: NewReturnShippingSelection;
  selectedDeflectionOffer?: DeflectionOffer;
  expectedFeeQuoteFingerprint: string;
  feeQuote: NewReturnFeeQuote;
  resolutionLabel: string;
  shippingLabel: string;
}

interface ReturnRequestOptionsProps {
  order: RequestOrder;
  items: NewRequestItemInput[];
  notes: string;
  policy: NewReturnPolicy;
  onContinue: (selection: ReturnRequestSelection) => void;
}

interface ResolutionChoice {
  key: string;
  resolution: string;
  label: string;
  keepsItems: boolean;
  offer?: DeflectionOffer;
}

interface ShippingContact {
  orderId: string;
  state: 'READY' | 'REQUIRES_PHONE' | 'INVALID';
  requiresReturnShippingPhone: boolean;
  phone?: string | null;
  message?: string | null;
}

function shippingSelection(
  option: NewReturnShippingOption,
): NewReturnShippingSelection {
  const {
    id,
    source,
    courierServiceId,
    rateId,
    carrier,
    service,
    displayName,
    amount,
    currency,
    meta,
  } = option;
  return {
    id,
    source,
    courierServiceId,
    rateId,
    carrier,
    service,
    displayName,
    amount,
    currency,
    meta,
  };
}

function validFeeQuote(
  quote: NewReturnFeeQuote | undefined,
  orderId: string,
  resolution: string,
): quote is NewReturnFeeQuote {
  return Boolean(
    quote &&
      quote.orderId === orderId &&
      quote.requestedResolution === resolution &&
      typeof quote.feeQuoteFingerprint === 'string' &&
      quote.feeQuoteFingerprint.trim() &&
      [quote.subtotal, quote.totalFees, quote.netAmount].every(
        (amount) =>
          typeof amount === 'number' && Number.isFinite(amount) && amount >= 0,
      ) &&
      Array.isArray(quote.dynamicFees) &&
      quote.dynamicFees.every(
        (fee) =>
          (fee.label === null || typeof fee.label === 'string') &&
          typeof fee.calculatedAmount === 'number' &&
          Number.isFinite(fee.calculatedAmount) &&
          fee.calculatedAmount >= 0,
      ),
  );
}

function formatAmount(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export function ReturnRequestOptions({
  order,
  items,
  policy,
  onContinue,
}: ReturnRequestOptionsProps) {
  const [choiceKey, setChoiceKey] = useState('');
  const [shippingId, setShippingId] = useState('');
  const [shippingAttempt, setShippingAttempt] = useState(0);
  const [feeAttempt, setFeeAttempt] = useState(0);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [shippingResult, setShippingResult] = useState<{
    key: string;
    quote?: NewReturnShippingQuote;
    error?: string;
    requiresPhone?: boolean;
  }>();
  const [prepareContact] = useMutation<{
    prepareReturnShippingContact: ShippingContact;
  }>(PREPARE_RETURN_SHIPPING_CONTACT);
  const [updateContact] = useMutation<{
    updateReturnOrderContact: ShippingContact;
  }>(UPDATE_RETURN_ORDER_CONTACT);
  const [feeResult, setFeeResult] = useState<{
    key: string;
    quote?: NewReturnFeeQuote;
    error?: string;
  }>();
  const [quoteShipping] = useLazyQuery<{
    quoteReturnShippingOptions: NewReturnShippingQuote;
  }>(QUOTE_NEW_RETURN_SHIPPING_OPTIONS, { fetchPolicy: 'network-only' });
  const [quoteFees] = useLazyQuery<{ quoteReturnFees: NewReturnFeeQuote }>(
    QUOTE_NEW_RETURN_FEES,
    { fetchPolicy: 'network-only' },
  );
  const selectedItemIdsKey = JSON.stringify(items.map(({ id }) => id).sort());
  const selectedItemIds = useMemo<string[]>(
    () => JSON.parse(selectedItemIdsKey),
    [selectedItemIdsKey],
  );
  const choices: ResolutionChoice[] = useMemo(() => {
    const allowed = new Set(
      (policy.allowedResolutions || []).map((value) => value.toUpperCase()),
    );
    const standard = [
      {
        key: 'REFUND',
        resolution: 'REFUND',
        label: 'Refund to original payment',
        keepsItems: false,
      },
      {
        key: 'STORE_CREDIT',
        resolution: 'STORE_CREDIT',
        label: 'Store credit',
        keepsItems: false,
      },
    ].filter((choice) => allowed.has(choice.resolution));
    const discountCodeLabelPattern = /\bdiscount code\b/i;
    const seenOffers = new Set<string>();
    const offers = (policy.deflectionOffers || []).flatMap(
      (offer): ResolutionChoice[] => {
        const type = offer.type?.toUpperCase();
        const keepsItems = [
          'KEEP_ITEM',
          'KEEP_ITEM_DISCOUNT',
          'KEEP_IT',
        ].includes(type);
        const bonus =
          type === 'STORE_CREDIT_BONUS' && allowed.has('STORE_CREDIT');
        const valueType = String(offer.valueType || '')
          .trim()
          .toUpperCase();
        const validValue =
          Number.isFinite(offer.value) &&
          offer.value > 0 &&
          ['PERCENTAGE', 'FIXED', 'FIXED_AMOUNT'].includes(valueType) &&
          (valueType !== 'PERCENTAGE' || offer.value <= 100);
        if ((!keepsItems && !bonus) || !validValue || !offer.label?.trim())
          return [];
        if (
          keepsItems &&
          Array.isArray(offer.eligibleOrderItemIds) &&
          (!selectedItemIds.length ||
            !selectedItemIds.every((id) =>
              offer.eligibleOrderItemIds!.includes(id),
            ))
        )
          return [];
        const offerId = offer.id ?? null;
        const legacyRewardKind =
          offer.offerType ??
          (keepsItems && discountCodeLabelPattern.test(offer.label)
            ? 'DISCOUNT_CODE'
            : 'STORE_CREDIT');
        const key = JSON.stringify([
          'offer',
          offerId,
          type,
          offerId === null ? legacyRewardKind : offer.offerType ?? null,
          offer.value,
          offer.valueType,
          offer.ruleId ?? null,
          offerId === null ? offer.label : null,
        ]);
        if (seenOffers.has(key)) return [];
        seenOffers.add(key);
        return [
          {
            key,
            resolution: keepsItems ? type : 'STORE_CREDIT',
            label: offer.label,
            keepsItems,
            offer,
          },
        ];
      },
    );
    return [...standard, ...offers];
  }, [policy, selectedItemIds]);
  const choice = choices.find((option) => option.key === choiceKey);
  useEffect(() => {
    if (choiceKey && !choice) {
      setChoiceKey('');
      setFeeResult(undefined);
    }
  }, [choiceKey, choice]);
  const selectedItemsEligible =
    items.length > 0 &&
    items.every((item) => {
      const eligibility = policy.items?.find(
        (candidate) => candidate.orderItemId === item.id,
      );
      return (
        eligibility?.eligible === true &&
        Number.isInteger(item.quantity) &&
        item.quantity > 0 &&
        item.quantity <= eligibility.availableQuantity &&
        item.quantity <= eligibility.maxReturnQuantity
      );
    });
  const eligible = policy.eligible === true && selectedItemsEligible;
  const requiresShipping = eligible && Boolean(choice && !choice.keepsItems);
  const orderedItems = useMemo(
    () => [...items].sort((left, right) => left.id.localeCompare(right.id)),
    [items],
  );
  const shippingInputKey = JSON.stringify({
    orderId: order.id,
    storeId: order.storeId,
    items: orderedItems.map(({ id, quantity }) => ({ id, quantity })),
  });
  const shippingKey = `${shippingInputKey}:${shippingAttempt}`;
  const currentOrder = useRef<{ id: string } | null>(null);
  useLayoutEffect(() => {
    // Each committed order visit gets its own identity, including A → B → A.
    currentOrder.current = { id: order.id };
    return () => {
      currentOrder.current = null;
    };
  }, [order.id]);
  useEffect(() => {
    setPhone('');
    setPhoneError('');
    setSavingPhone(false);
  }, [order.id]);
  useEffect(() => {
    if (!requiresShipping) return;
    let active = true;
    setShippingId('');
    setPhoneError('');
    prepareContact({ variables: { input: { orderId: order.id } } })
      .then((result) => {
        if (!active) return;
        const contact = result.data?.prepareReturnShippingContact;
        if (result.errors?.length || !contact || contact.orderId !== order.id) {
          throw new Error('Shipping contact could not be verified.');
        }
        if (contact.state !== 'READY' || contact.requiresReturnShippingPhone) {
          setShippingResult({
            key: shippingKey,
            requiresPhone: true,
            error:
              contact.message ||
              'Add a valid phone number for return shipping.',
          });
          return;
        }
        return quoteShipping({
          variables: { input: JSON.parse(shippingInputKey) },
        });
      })
      .then((result) => {
        if (!active || !result) return;
        const quote = result.data?.quoteReturnShippingOptions;
        if (
          result.error ||
          !quote ||
          quote.orderId !== order.id ||
          quote.storeId !== order.storeId
        ) {
          setShippingResult({
            key: shippingKey,
            error: 'Unable to load return shipping options. Please retry.',
          });
        } else if (
          quote.requiresReturnShippingPhone ||
          quote.errorCode === 'RETURN_SHIPPING_PHONE_REQUIRED' ||
          quote.errorCode === 'RETURN_SHIPPING_PHONE_INVALID'
        ) {
          setShippingResult({
            key: shippingKey,
            requiresPhone: true,
            error:
              quote.errorMessage ||
              'Add a valid phone number for return shipping.',
          });
        } else if (quote.errorCode || quote.errorMessage) {
          setShippingResult({
            key: shippingKey,
            error:
              quote.errorMessage ||
              'Return shipping is unavailable. Check the order shipping details and retry.',
          });
        } else if (
          !quote.options.length &&
          quote.sources.some(
            (source) =>
              source.status === 'FAILED' || source.status === 'SKIPPED',
          )
        ) {
          setShippingResult({
            key: shippingKey,
            error:
              quote.sources.find((source) => source.message)?.message ||
              'Return shipping is unavailable. Check the order shipping details and retry.',
          });
        } else {
          setShippingResult({ key: shippingKey, quote });
        }
      })
      .catch(() => {
        if (active)
          setShippingResult({
            key: shippingKey,
            error: 'Unable to load return shipping options. Please retry.',
          });
      });
    return () => {
      active = false;
    };
  }, [
    requiresShipping,
    shippingInputKey,
    shippingKey,
    quoteShipping,
    prepareContact,
    order.id,
    order.storeId,
  ]);
  const currentShipping =
    shippingResult?.key === shippingKey ? shippingResult : undefined;

  const saveShippingPhone = async () => {
    if (!phone.trim() || savingPhone) return;
    const savingOrder = currentOrder.current;
    if (!savingOrder || savingOrder.id !== order.id) return;
    const savingOrderId = savingOrder.id;
    setSavingPhone(true);
    setPhoneError('');
    try {
      const result = await updateContact({
        variables: { input: { orderId: savingOrderId, phone: phone.trim() } },
      });
      if (currentOrder.current !== savingOrder) return;
      const contact = result.data?.updateReturnOrderContact;
      if (
        result.errors?.length ||
        !contact ||
        contact.orderId !== savingOrderId
      ) {
        throw new Error('Shipping phone could not be saved.');
      }
      if (contact.state !== 'READY' || contact.requiresReturnShippingPhone) {
        setPhoneError(
          contact.message || 'Enter a valid phone number with a country code.',
        );
        return;
      }
      setShippingAttempt((value) => value + 1);
    } catch {
      if (currentOrder.current === savingOrder) {
        setPhoneError(
          'Unable to save the shipping phone. Check the number and try again.',
        );
      }
    } finally {
      if (currentOrder.current === savingOrder) setSavingPhone(false);
    }
  };
  const shippingOptions = (currentShipping?.quote?.options || [])
    .filter(
      (option) =>
        option.customerVisible === true &&
        Boolean(option.id && option.source && option.courierServiceId) &&
        (option.amount == null ||
          (Number.isFinite(option.amount) && option.amount >= 0)),
    )
    .sort((left, right) => left.sortOrder - right.sortOrder);
  const selectedShipping = shippingOptions.find(
    (option) => option.id === shippingId,
  );
  const deferredShipping =
    shippingOptions.length === 0 &&
    Boolean(currentShipping?.quote) &&
    shippingId === 'defer';
  const readyToQuote =
    eligible &&
    Boolean(
      choice && (choice.keepsItems || selectedShipping || deferredShipping),
    ) &&
    (!requiresShipping || !currentShipping?.error);
  const feeInputKey = JSON.stringify({
    orderId: order.id,
    storeId: order.storeId,
    requestedResolution: choice?.resolution,
    ...(choice?.offer ? { deflectionOption: choice.offer.type } : {}),
    ...(selectedShipping && !choice?.keepsItems
      ? { selectedShippingOption: shippingSelection(selectedShipping) }
      : {}),
    items: orderedItems.map(({ id, quantity, reason }) => ({
      id,
      quantity,
      reason,
      requestedResolution: choice?.resolution,
    })),
  });
  const feeResolution = choice?.resolution;
  const feeKey = `${feeInputKey}:${choice?.key || ''}:${feeAttempt}`;
  useEffect(() => {
    if (!readyToQuote || !feeResolution) return;
    let active = true;
    quoteFees({ variables: { input: JSON.parse(feeInputKey) } })
      .then((result) => {
        if (!active) return;
        const quote = result.data?.quoteReturnFees;
        if (!result.error && validFeeQuote(quote, order.id, feeResolution)) {
          setFeeResult({ key: feeKey, quote });
        } else {
          setFeeResult({
            key: feeKey,
            error:
              'Unable to verify the current return fee quote. Please retry.',
          });
        }
      })
      .catch(() => {
        if (active)
          setFeeResult({
            key: feeKey,
            error: 'Unable to load the return fee quote. Please retry.',
          });
      });
    return () => {
      active = false;
    };
  }, [readyToQuote, feeInputKey, feeKey, quoteFees, order.id, feeResolution]);
  const currentFees = feeResult?.key === feeKey ? feeResult : undefined;
  const feeQuote = readyToQuote ? currentFees?.quote : undefined;
  const currency = feeQuote?.currency || order.currencyCode || 'USD';
  const shippingLabel = choice?.keepsItems
    ? 'Customer keeps the items'
    : selectedShipping?.displayName ||
      [selectedShipping?.carrier, selectedShipping?.service]
        .filter(Boolean)
        .join(' ') ||
      'Arrange shipping after review';

  const continueToReview = () => {
    if (!choice || !eligible || !feeQuote) return;
    onContinue({
      requestedResolution: choice.resolution,
      ...(choice.offer
        ? {
            deflectionOption: choice.offer.type,
            selectedDeflectionOffer: choice.offer,
          }
        : {}),
      ...(selectedShipping && !choice.keepsItems
        ? { selectedShippingOption: shippingSelection(selectedShipping) }
        : {}),
      expectedFeeQuoteFingerprint: feeQuote.feeQuoteFingerprint,
      feeQuote,
      resolutionLabel: choice.label,
      shippingLabel,
    });
  };

  return (
    <section className="space-y-5" aria-label="Return resolution and shipping">
      <div>
        <p className="mt-1 text-sm text-gray-600">
          Choose the requested outcome and review the current policy fees.
        </p>
      </div>
      {!eligible ? (
        <div
          role="alert"
          className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
        >
          {policy.ineligibleReason ||
            'Some selected items or quantities are no longer eligible. Go back and review the items.'}
        </div>
      ) : (
        <>
          <fieldset className="space-y-2">
            <legend className="mb-2 text-sm font-semibold">
              Requested resolution
            </legend>
            {choices.map((option) => (
              <label
                key={option.key}
                className="flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 p-3 text-sm"
              >
                <input
                  type="radio"
                  name="return-resolution"
                  className="mt-0.5"
                  checked={choiceKey === option.key}
                  onChange={() => setChoiceKey(option.key)}
                />
                <span>
                  {option.label}
                  {option.keepsItems && (
                    <span className="block text-xs text-gray-500">
                      No shipment back is required.
                    </span>
                  )}
                </span>
              </label>
            ))}
            {choices.length === 0 && (
              <p role="alert" className="text-sm text-amber-800">
                No supported return resolution is currently available for this
                order.
              </p>
            )}
          </fieldset>
          {(policy.unavailableResolutions || []).map((resolution, index) => (
            <p
              key={`${resolution.resolution}:${index}`}
              className="text-sm text-gray-600"
            >
              {resolution.message}
            </p>
          ))}
          {requiresShipping && (
            <fieldset className="space-y-2">
              <legend className="mb-2 text-sm font-semibold">
                Return shipping
              </legend>
              {!currentShipping && (
                <p role="status" className="text-sm text-gray-600">
                  Loading return shipping options…
                </p>
              )}
              {currentShipping?.error && (
                <div role="alert" className="space-y-2 text-sm text-red-700">
                  <p>{currentShipping.error}</p>
                  {currentShipping.requiresPhone ? (
                    <div className="space-y-2">
                      <label className="block text-gray-900">
                        Return shipping phone
                        <input
                          type="tel"
                          autoComplete="tel"
                          maxLength={100}
                          value={phone}
                          disabled={savingPhone}
                          onChange={(event) => setPhone(event.target.value)}
                          className="mt-1 block w-full rounded-md border border-gray-300 p-2"
                        />
                      </label>
                      <p className="text-gray-600">
                        Include the country code. This updates only the order
                        shipping phone in Shopify.
                      </p>
                      {phoneError && <p>{phoneError}</p>}
                      <Button
                        disabled={!phone.trim() || savingPhone}
                        onClick={saveShippingPhone}
                      >
                        {savingPhone
                          ? 'Saving phone…'
                          : 'Save phone and load shipping'}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={() => setShippingAttempt((value) => value + 1)}
                    >
                      Retry shipping options
                    </Button>
                  )}
                </div>
              )}
              {shippingOptions.map((option) => (
                <label
                  key={option.id}
                  className="flex cursor-pointer items-center gap-3 rounded-md border border-gray-200 p-3 text-sm"
                >
                  <input
                    type="radio"
                    name="return-shipping"
                    checked={shippingId === option.id}
                    onChange={() => setShippingId(option.id)}
                  />
                  <span>
                    {option.displayName ||
                      [option.carrier, option.service]
                        .filter(Boolean)
                        .join(' ') ||
                      'Return shipping'}
                    <span className="block text-xs text-gray-500">
                      Customer fees appear in the estimate below.
                    </span>
                  </span>
                </label>
              ))}
              {currentShipping?.quote && shippingOptions.length === 0 && (
                <label className="flex cursor-pointer items-center gap-3 rounded-md border border-gray-200 p-3 text-sm">
                  <input
                    type="radio"
                    name="return-shipping"
                    checked={shippingId === 'defer'}
                    onChange={() => setShippingId('defer')}
                  />
                  <span>
                    Arrange shipping after review
                    <span className="block text-xs text-gray-500">
                      No shipping service is available to select for this
                      request.
                    </span>
                  </span>
                </label>
              )}
            </fieldset>
          )}
          {choice?.keepsItems && (
            <p className="rounded-md bg-blue-50 p-3 text-sm text-blue-900">
              Customer keeps the items. Review the selected offer before
              submitting.
            </p>
          )}
          {readyToQuote && !currentFees && (
            <p role="status" className="text-sm text-gray-600">
              Calculating current return fees…
            </p>
          )}
          {readyToQuote && currentFees?.error && (
            <div role="alert" className="space-y-2 text-sm text-red-700">
              <p>{currentFees.error}</p>
              <Button
                variant="outlined"
                onClick={() => setFeeAttempt((value) => value + 1)}
              >
                Retry fee quote
              </Button>
            </div>
          )}
          {feeQuote && (
            <section
              aria-label="Return estimate"
              className="rounded-md border border-gray-200 p-4"
            >
              <h3 className="font-semibold">Return estimate</h3>
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt>Selected items</dt>
                  <dd>{formatAmount(feeQuote.subtotal, currency)}</dd>
                </div>
                {feeQuote.dynamicFees.map((fee, index) => (
                  <div
                    key={`${fee.label}:${index}`}
                    className="flex justify-between gap-4"
                  >
                    <dt>{fee.label?.trim() || 'Return fee'}</dt>
                    <dd>{formatAmount(fee.calculatedAmount || 0, currency)}</dd>
                  </div>
                ))}
                <div className="flex justify-between gap-4">
                  <dt>Total fees</dt>
                  <dd>{formatAmount(feeQuote.totalFees, currency)}</dd>
                </div>
                <div className="flex justify-between gap-4 border-t pt-2 font-semibold">
                  <dt>
                    {choice?.keepsItems
                      ? 'Return value after fees'
                      : choice?.resolution === 'STORE_CREDIT'
                      ? 'Estimated store credit'
                      : 'Estimated refund'}
                  </dt>
                  <dd>{formatAmount(feeQuote.netAmount, currency)}</dd>
                </div>
              </dl>
              <p className="mt-3 text-xs text-gray-500">
                Final processing follows the return policy and approval
                requirements.
              </p>
            </section>
          )}
        </>
      )}
      <div className="flex justify-end">
        <Button disabled={!feeQuote || !eligible} onClick={continueToReview}>
          Review return
        </Button>
      </div>
    </section>
  );
}
